import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  FiduciaryEscalationRequest,
  FiduciaryEscalationResponse,
} from '$/requests/FiduciaryEscalation.ts';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';

const useFiduciaryEscalation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: FiduciaryEscalationRequest) => submitFiduciaryEscalation(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
    },
  });
};

export default useFiduciaryEscalation;

async function submitFiduciaryEscalation(
  body: FiduciaryEscalationRequest,
): Promise<FiduciaryEscalationResponse> {
  const { data, error } = await supabaseClient.functions.invoke('fiduciary-escalation', { body });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      const errorResponse: BackendErrorResponse = await error.context.json();
      throw new Error(errorResponse.errors?.join(', ') ?? 'Escalation failed');
    }
    throw new Error(error.message || 'Escalation failed');
  }

  const payload = data as { data?: FiduciaryEscalationResponse; errors?: string[] };
  if (payload?.errors?.length) {
    throw new Error(payload.errors.join(', '));
  }
  if (!payload?.data) {
    throw new Error('Escalation failed');
  }

  return payload.data;
}
